import { useEffect, useMemo, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { cn } from '@/utils/cn';

function parseRegistrationCodes(value: string): string[] {
  return value
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean);
}

const pullRegistrationSchema = z.object({
  registrationCodes: z
    .string()
    .min(1, 'Enter RegistrationCode')
    .refine((value) => parseRegistrationCodes(value).length > 0, 'Enter RegistrationCode'),
});

type PullRegistrationFormValues = z.infer<typeof pullRegistrationSchema>;

interface PullRegistrationDialogProps {
  open: boolean;
  eventCode: string;
  accountCode: string;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (registrationCodes: string[]) => Promise<void> | void;
}

function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => !element.hasAttribute('disabled') && element.tabIndex !== -1);
}

export function PullRegistrationDialog({
  open,
  eventCode,
  accountCode,
  isSubmitting,
  onClose,
  onSubmit,
}: PullRegistrationDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useMemo(() => 'pull-registration-dialog-title', []);
  const descriptionId = useMemo(() => 'pull-registration-dialog-description', []);
  const errorId = useMemo(() => 'registration-codes-error', []);

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<PullRegistrationFormValues>({
    resolver: zodResolver(pullRegistrationSchema),
    defaultValues: { registrationCodes: '' },
  });

  const registrationCodesField = register('registrationCodes');

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(focusTimer);
      previousFocusRef.current?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) reset({ registrationCodes: '' });
  }, [open, reset]);

  if (!open) return null;

  const submitForm = handleSubmit(async (values) => {
    await onSubmit(parseRegistrationCodes(values.registrationCodes));
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="w-full max-w-md border border-cool-gray-2 bg-white shadow-xl"
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !isSubmitting) onClose();
          if (event.key !== 'Tab') return;
          const focusable = getFocusableElements(dialogRef.current);
          if (focusable.length === 0) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }}
      >
        <div className="flex items-center justify-between bg-dds-blue px-4 py-3 text-white">
          <h2 id={titleId} className="text-base font-bold">
            Pull Registrations
          </h2>
          <button
            type="button"
            aria-label="Close pull registrations dialog"
            className="inline-flex h-8 w-8 items-center justify-center hover:bg-black/20 focus:outline-none focus:ring-2 focus:ring-white"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>

        <form className="space-y-4 p-4" onSubmit={(event) => void submitForm(event)}>
          <div id={descriptionId} className="space-y-1 text-sm text-black">
            <p>EventCode : {eventCode}</p>
            <p>AccountCode : {accountCode}</p>
          </div>

          <div>
            <label htmlFor="registrationCodes" className="block text-sm font-semibold text-black">
              RegistrationCode
            </label>
            <input
              id="registrationCodes"
              type="text"
              className={cn(
                'mt-1 block w-full border-cool-gray-7 text-sm focus:border-dds-blue focus:ring-dds-blue',
                errors.registrationCodes && 'border-dds-red focus:border-dds-red focus:ring-dds-red',
              )}
              aria-invalid={errors.registrationCodes ? 'true' : 'false'}
              aria-describedby={errors.registrationCodes ? errorId : undefined}
              disabled={isSubmitting}
              {...registrationCodesField}
              ref={(element) => {
                registrationCodesField.ref(element);
                inputRef.current = element;
              }}
            />
            {errors.registrationCodes ? (
              <p id={errorId} role="alert" className="mt-1 text-sm text-dds-red">
                {errors.registrationCodes.message}
              </p>
            ) : null}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="inline-flex h-9 items-center border border-cool-gray-7 px-4 text-sm font-semibold text-black hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex h-9 items-center gap-2 bg-dds-blue px-4 text-sm font-semibold text-white hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
              ) : null}
              Pull Reg
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}